import KPIData from 'models/kpi/kpiData';
import type KPI from 'models/kpi/kpi';
import type TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import type { TFunction } from 'i18next';
import type { IFormStructureAddOption } from 'components/Tasks/Forms/FormStructureAddControl';
import { sortOnNumber } from 'utils/sorting';

type FormReuseCandidate = Pick<KPI, 'kpiId' | 'isSystemType'>;
type FormElementKPIReference = Pick<TaskTypeFormElement, 'kpiId'>;

export const filterFormReuseCandidates = <T extends FormReuseCandidate>(
  kpis: T[],
  formElements: FormElementKPIReference[],
): T[] =>
  kpis.filter(
    (kpi) => !kpi.isSystemType() && !formElements.some((element) => element.kpiId === kpi.kpiId),
  );

// A form being DESIGNED renders through the same components as a form being answered: the fields must
// arrive in sort order and every KPI field needs a data row to render into. Neither a GET of the form
// nor the elements an accepted structure mutation hands back are guaranteed to have both - the discard
// response is the raw server projection - so both paths run through here.
//
// Seeding is conditional because KPI.addData APPENDS: an accepted element is a clone of one already on
// screen and already carries its row, and a second one would give the field two answers to render.
export const prepareDesignTimeFormElements = (elements: TaskTypeFormElement[]): TaskTypeFormElement[] => {
  const prepared = [...elements].sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder));

  prepared.forEach((element) => {
    if (element.kpi && element.kpi.data.length === 0) {
      element.kpi.addData([new KPIData()]);
    }
  });

  return prepared;
};

// Discarding an edit session acts on the form being designed, so it belongs on that form's own toolbar
// next to Add - not on the scene's command bar, which acts on the LIST of forms. It stays on the
// toolbar with nothing to discard (a command that comes and goes cannot be found when it is wanted);
// the title then says why it is off.
export const buildFormDiscardCommand = (
  t: TFunction,
  canDiscard: boolean,
  onDiscard: () => void,
): IFormStructureAddOption => ({
  key: 'discard',
  icon: 'Undo',
  label: t('translation:General.Button.Discard'),
  onClick: onDiscard,
  disabled: !canDiscard,
  title: canDiscard
    ? t('forms:Versioning.Autosave.DiscardInfo')
    : t('forms:Versioning.Autosave.DiscardDisabledInfo'),
});
